import { Box } from "@mui/material"
import { useParams } from "@tanstack/react-router"
import type { ChangeEvent, FC } from "react"

import { useProductInventoryContext } from "../../contexts"
import { useProductInventoryPalletList } from "../../hooks"

import { ProductInventoryPalletsTable } from "./ProductInventoryPalletsTable"

import SearchInput from "@/components/SearchInput/SearchInput"
import { PermissionActionType } from "@/constants/permissions"
import type { InventoryByPalletItem } from "@/graphql/codegen/graphql"
import { usePermissionManager } from "@/hooks/usePermissionManager"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

export const ProductInventoryPalletsTab: FC = () => {
  const { productId } = useParams({ from: "/app/inventory/products/$productId/_layout/pallets" })
  const { product } = useProductInventoryContext()
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const { hasPermissions } = usePermissionManager()
  const canMaintain = hasPermissions([PermissionActionType.INVENTORY_MAINTENANCE])

  const {
    inventories,
    isFetching,
    error,
    sorting,
    searchParams,
    tableContainerRef,
    handleSortingChange,
    handleSearchChange,
    handleScroll,
  } = useProductInventoryPalletList({ productId: Number(productId) })

  const handleHoldPut = (_item: InventoryByPalletItem) => {
    notify({ type: NotificationType.Warning, message: "Not implemented yet" })
  }

  const handleHoldRelease = (_item: InventoryByPalletItem) => {
    notify({ type: NotificationType.Warning, message: "Not implemented yet" })
  }

  const handleTransfer = (_item: InventoryByPalletItem) => {
    notify({ type: NotificationType.Warning, message: "Not implemented yet" })
  }

  const handleCombinePallets = (_item: InventoryByPalletItem) => {
    notify({ type: NotificationType.Warning, message: "Not implemented yet" })
  }

  const handleCoaView = (_item: InventoryByPalletItem) => {
    notify({ type: NotificationType.Warning, message: "Not implemented yet" })
  }

  return (
    <Box className='flex h-full flex-col gap-y-6'>
      <SearchInput
        placeholder='Search by Lot or Pallet'
        defaultValue={searchParams.query}
        onChange={(e: ChangeEvent<HTMLInputElement>) => handleSearchChange(e.target.value)}
        className='w-full max-w-[524px]'
        data-testid='product-inventory.pallets-tab.search-field'
      />
      <Box className='flex min-h-0 flex-1 overflow-y-auto'>
        <ProductInventoryPalletsTable
          product={product}
          data={inventories}
          loading={isFetching}
          error={error}
          sorting={sorting}
          containerRef={tableContainerRef}
          onSortingChange={handleSortingChange}
          onScroll={handleScroll}
          canMaintain={canMaintain}
          onHoldPut={handleHoldPut}
          onHoldRelease={handleHoldRelease}
          onTransfer={handleTransfer}
          onCombinePallets={handleCombinePallets}
          onCoaView={handleCoaView}
        />
      </Box>
    </Box>
  )
}
